import { Trans } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';

import { Box } from '@mui/system';
import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import { Alert, Select, Checkbox, MenuItem, TextField, InputLabel, Typography, FormControlLabel } from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { UserManagedRejectionReasons } from 'src/services/applications/applications.utils';
import { useLazyGetApplicationByIdQuery } from 'src/services/applications/applications.service';
import { RejectionReason, RepresentationStatus } from 'src/services/applications/applications.types';
import { IRejectionTemplate, OrganizationSetting } from 'src/services/organisation/organisation.types';
import { useGetOrganizationSettingsQuery, useGetRejectionTemplateSettingsQuery } from 'src/services/organisation/organisation.service';

import { ConfirmDialog } from 'src/components/custom-dialog';

type Props = {
  open: boolean;
  applications: string[],
  onConfirm: (rejectionReason: RejectionReason, rejectionMessage: string, shouldNotify: boolean) => void;
  onClose: VoidFunction;
};

export default function ApplicationRejectionConfirm({ open, onConfirm, onClose, applications = [] }: Props) {

  const { organization } = useAuthContext();

  const { t } = useTranslate();

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [rejectionReason, setRejectionReason] = useState<RejectionReason>();
  const [rejectionFeedback, setRejectionFeedback] = useState<string>('');
  const [shouldNotify, setShouldNotify] = useState<boolean>(true);
  const [showSilentRejectionSelector, setShowSilentRejectionSelector] = useState<boolean>(false);

  // Local applicants have no JobTetris account, so the platform never notifies them (JT1-1576).
  // Until the selection has loaded, assume a live candidate, which is how the dialog always behaved.
  const [hasLiveApplicant, setHasLiveApplicant] = useState<boolean>(true);
  const [hasLocalApplicant, setHasLocalApplicant] = useState<boolean>(false);

  const { currentData: settingsData } = useGetOrganizationSettingsQuery(
    organization.id
  );

  const { currentData: rejectionTemplates } = useGetRejectionTemplateSettingsQuery(
    organization.id
  );

  const [getApplicationById] = useLazyGetApplicationByIdQuery();

  const rejectionReasonsEditableSetting = useMemo(() => {

    if (settingsData) {
      const rejectionTemplateSetting = settingsData.find(
        (setting) => setting.setting_type === OrganizationSetting.REJECTION_TEMPLATE_MESSAGES_EDITABLE
      );

      if (rejectionTemplateSetting) {
        return rejectionTemplateSetting.setting_value as boolean;
      }
    }

    return false;
  }, [settingsData]);

  const silentRejectionAllowedSetting = useMemo(() => {

    if (settingsData) {
      const silentRejectionSetting = settingsData.find(
        (setting) => setting.setting_type === OrganizationSetting.SILENT_REJECTION_ENABLED
      );

      if (silentRejectionSetting) {
        return silentRejectionSetting.setting_value as boolean;
      }
    }

    return false;

  }, [settingsData]);

  // A local applicant is only emailed when the organisation has chosen a rejection template.
  const localRejectionEmailConfigured = useMemo(() => {
    const templateSetting = settingsData?.find(
      (setting) => setting.setting_type === OrganizationSetting.LOCAL_REJECTION_TEMPLATE_ID
    );

    return !!templateSetting?.setting_value;
  }, [settingsData]);

  const willNotifyAnyone = hasLiveApplicant || (hasLocalApplicant && localRejectionEmailConfigured);

  useEffect(() => {
    const inspectSelection = async () => {
      try {
        const selected = await Promise.all(
          applications.filter(Boolean).map((applicationId) => getApplicationById(applicationId, true).unwrap())
        );

        setHasLiveApplicant(selected.some((application) => application.representation_status === RepresentationStatus.ACTIVE));
        setHasLocalApplicant(selected.some((application) => application.representation_status !== RepresentationStatus.ACTIVE));

        // to show the silent rejection selector, the following conditions must be met:
        // 1. silent rejection must be allowed in organization settings
        // 2. only one application is being rejected in this action
        // 3. the current user is the manager of the application (not a client in tri party application)
        setShowSilentRejectionSelector(
          selected.length === 1 && silentRejectionAllowedSetting && selected[0].manager.id === organization.id
        );
      }
      catch (error) {
        // fall back to the dialog as it was before local applicants were considered
        setHasLiveApplicant(true);
        setHasLocalApplicant(false);
        setShowSilentRejectionSelector(false);
      }
    }

    if (open) {
      inspectSelection();
    }

  }, [open, applications, silentRejectionAllowedSetting, getApplicationById, organization]);

  useEffect(() => {
    if (rejectionTemplates && rejectionTemplates.length > 0) {
      const selectedTemplate: IRejectionTemplate = rejectionTemplates.find((template) => template.reason === rejectionReason) as IRejectionTemplate;

      if (selectedTemplate) {
        setRejectionFeedback(selectedTemplate.message);
      }
    }
  }, [rejectionReason, rejectionTemplates]);

  const handleReject = () => {
    setErrorMessage('');
    if (!rejectionReason) {
      setErrorMessage(t('applications.rejection.reason_required'));
      return;
    }

    onConfirm(rejectionReason as RejectionReason, rejectionFeedback, shouldNotify);
  }

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={t('common.confirm')}
      content={
        <Stack spacing={2} direction="column">
          <Box>
            <Trans>
              {t('applications.selected', { count: applications.length })}
            </Trans>
          </Box>

          {
            hasLiveApplicant && (
              <Box>
                {t('applications.stage_change.reject_applications')}
              </Box>
            )
          }

          {
            // nothing is said about local applicants unless they are actually going to be emailed
            hasLocalApplicant && localRejectionEmailConfigured && (
              <Box>
                {t('applications.rejection.local_email')}
              </Box>
            )
          }

          {
            errorMessage && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {errorMessage}
              </Typography>
            )
          }

          <Stack>
            <InputLabel required>{t('applications.rejection.reason_label')}</InputLabel>
            <Select
              required
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value as RejectionReason)}
            >
              {UserManagedRejectionReasons.map((reason) => (
                <MenuItem key={reason.value} value={reason.value}>
                  {reason.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>

          {
            // feedback only goes somewhere when someone is going to be told about the rejection
            willNotifyAnyone && (
              <Stack>
                <InputLabel>{t('applications.rejection.feedback_label')}</InputLabel>
                <TextField
                  value={rejectionFeedback}
                  disabled={!rejectionReasonsEditableSetting}
                  onChange={(e) => setRejectionFeedback(e.target.value)}
                  multiline
                  rows={6}
                />
                {
                  !rejectionReasonsEditableSetting && (
                    <Alert severity="info" icon={false} sx={{ mt: 2 }}>
                      {t('applications.rejection.editable_info')}
                    </Alert>
                  )
                }
              </Stack>
            )
          }

          {
            showSilentRejectionSelector && willNotifyAnyone && (
              <Stack>
                <FormControlLabel
                  label={
                    <Stack gap={0} sx={{ ml: 1 }}>
                      <Typography variant="body2">
                        {t('applications.rejection.should_notify')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {hasLiveApplicant
                          ? t('applications.rejection.should_notify_helper')
                          : t('applications.rejection.should_notify_helper_local')}
                      </Typography>
                    </Stack>
                  }
                  control={<Checkbox checked={shouldNotify} onChange={(e) => setShouldNotify(e.target.checked)} />}
                />
              </Stack>
            )
          }
        </Stack>
      }
      action={
        <LoadingButton variant="contained" color="error" onClick={handleReject}>
          {t('common.confirm')}
        </LoadingButton>
      }
    />
  );
}
